import React from 'react';
import { ArrowDown } from 'lucide-react';
import { cn } from '../cn';
function directionBadgeClass(tone) {
    switch (tone) {
        case 'in':
            return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100';
        case 'out':
            return 'bg-red-50 text-red-800 ring-1 ring-red-100';
        case 'fee':
            return 'bg-violet-50 text-violet-800 ring-1 ring-violet-100';
        case 'system':
            return 'bg-blue-50 text-blue-800 ring-1 ring-blue-100';
        default:
            return 'bg-gray-50 text-gray-700 ring-1 ring-gray-100';
    }
}
function directionBadgeLabel(tone) {
    const labels = {
        in: 'IN',
        out: 'OUT',
        fee: 'FEE',
        system: 'SYSTEM',
    };
    return labels[tone];
}
function amountClass(direction) {
    return direction === 'in' ? 'text-emerald-700' : 'text-red-700';
}
function statusClass(status) {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'success' || normalized === 'completed' || normalized === 'approved') {
        return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100';
    }
    if (normalized === 'failed' || normalized === 'rejected' || normalized === 'cancelled') {
        return 'bg-red-50 text-red-800 ring-1 ring-red-100';
    }
    if (normalized === 'pending') {
        return 'bg-amber-50 text-amber-800 ring-1 ring-amber-100';
    }
    return 'bg-gray-50 text-gray-700 ring-1 ring-gray-100';
}
function formatStatusLabel(status) {
    const s = String(status);
    if (!s)
        return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
function ownerLine(ownerType, ownerName) {
    const type = (ownerType || '').trim();
    const name = (ownerName || '').trim();
    if (type && name)
        return `${type}: ${name}`;
    if (name)
        return name;
    if (type)
        return type;
    return null;
}
function balanceLine(balance) {
    var _a;
    if (!balance)
        return null;
    if ((_a = balance.formatted) === null || _a === void 0 ? void 0 : _a.trim())
        return balance.formatted.trim();
    if (balance.before && balance.after)
        return `${balance.before} → ${balance.after}`;
    if (balance.before)
        return balance.before;
    if (balance.after)
        return balance.after;
    return null;
}
function DetailBox({ label, children }) {
    return (React.createElement("div", { className: "min-w-0 rounded-md bg-gray-50 px-2.5 py-2" },
        React.createElement("span", { className: "block text-[11px] font-semibold uppercase tracking-wide text-gray-500" }, label),
        React.createElement("strong", { className: "mt-0.5 block text-xs font-semibold text-gray-900 break-words" }, children)));
}
function DirectionPill({ tone }) {
    return (React.createElement("span", { className: cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold', directionBadgeClass(tone)) }, directionBadgeLabel(tone)));
}
function StepDetails({ direction, actionLabel, balance, status, timestamp, stepStatusLabel = 'Status', formatStepStatus = formatStatusLabel, }) {
    const balanceText = balanceLine(balance);
    const boxes = [];
    if (actionLabel === null || actionLabel === void 0 ? void 0 : actionLabel.trim()) {
        boxes.push({ key: 'action', label: 'Action', content: actionLabel.trim() });
    }
    else {
        boxes.push({ key: 'direction', label: 'Direction', content: React.createElement(DirectionPill, { tone: direction }) });
    }
    if (status) {
        boxes.push({
            key: 'status',
            label: stepStatusLabel,
            content: (React.createElement("span", { className: cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold', statusClass(status)) }, formatStepStatus(String(status)))),
        });
    }
    if (balance === null || balance === void 0 ? void 0 : balance.before) {
        boxes.push({ key: 'before', label: 'Before', content: balance.before });
    }
    if (balance === null || balance === void 0 ? void 0 : balance.after) {
        boxes.push({ key: 'after', label: 'After', content: balance.after });
    }
    else if (balanceText && !(balance === null || balance === void 0 ? void 0 : balance.before)) {
        boxes.push({ key: 'balance', label: 'Balance', content: balanceText });
    }
    if (timestamp === null || timestamp === void 0 ? void 0 : timestamp.trim()) {
        boxes.push({ key: 'time', label: 'Time', content: timestamp.trim() });
    }
    if (boxes.length === 0)
        return null;
    return (React.createElement("div", { className: "mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" }, boxes.map((box) => (React.createElement(DetailBox, { key: box.key, label: box.label }, box.content)))));
}
function BranchCard({ branch, formatStepStatus = formatStatusLabel, }) {
    var _a, _b;
    const owner = ownerLine(branch.ownerType, branch.ownerName);
    const badgeTone = (_a = branch.badge) !== null && _a !== void 0 ? _a : branch.direction;
    return (React.createElement("section", { className: "relative min-w-0 rounded-lg border border-gray-200 bg-white p-3 before:absolute before:-top-3 before:left-5 before:h-3 before:w-0.5 before:bg-gray-200" },
        React.createElement("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" },
            React.createElement("div", { className: "min-w-0" },
                React.createElement("h3", { className: "text-sm font-bold text-gray-900" }, branch.walletName),
                branch.subtitle ? React.createElement("p", { className: "mt-0.5 text-xs text-gray-500" }, branch.subtitle) : null,
                owner && !((_b = branch.subtitle) === null || _b === void 0 ? void 0 : _b.includes(owner)) ? (React.createElement("p", { className: "mt-0.5 text-xs text-gray-500" }, owner)) : null),
            React.createElement("p", { className: cn('shrink-0 text-sm font-bold tabular-nums', amountClass(branch.direction)) }, branch.amount)),
        (branch.actionLabel || branch.badge) && (React.createElement("p", { className: "mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-600" },
            React.createElement(DirectionPill, { tone: badgeTone }),
            branch.actionLabel ? React.createElement("span", null, branch.actionLabel) : null)),
        branch.status ? (React.createElement("div", { className: "mt-2" },
            React.createElement("span", { className: cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold', statusClass(branch.status)) }, formatStepStatus(String(branch.status))))) : null));
}
function TimelineNodeCard({ step, stepNumber, stepStatusLabel = 'Status', formatStepStatus = formatStatusLabel, }) {
    var _a, _b, _c, _d;
    const owner = ownerLine(step.ownerType, step.ownerName);
    const isSplit = step.variant === 'split' && ((_b = (_a = step.branches) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
    return (React.createElement("article", { className: "relative grid grid-cols-[44px_minmax(0,1fr)] gap-3" },
        React.createElement("div", { className: "relative z-[1] flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-bold text-orange-600", "aria-hidden": true }, stepNumber),
        React.createElement("div", { className: cn('mb-4 min-w-0 rounded-lg border border-gray-200 bg-white p-3', step.variant === 'source' && 'border-orange-200 bg-gradient-to-b from-orange-50/80 to-white', step.variant === 'split' && 'bg-gray-50/50') },
            React.createElement("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" },
                React.createElement("div", { className: "min-w-0" },
                    React.createElement("p", { className: "text-[15px] font-bold text-gray-900 break-words" }, step.walletName),
                    step.subtitle ? (step.subtitleHref ? (React.createElement("a", { href: step.subtitleHref, className: "mt-0.5 block text-xs text-gray-600 hover:underline" }, step.subtitle)) : (React.createElement("p", { className: "mt-0.5 text-xs text-gray-500" }, step.subtitle))) : null,
                    owner ? React.createElement("p", { className: "mt-0.5 text-xs text-gray-500" },
                        "Owner: ",
                        owner) : null,
                    isSplit && step.splitDescription ? (React.createElement("p", { className: "mt-1 text-xs text-gray-500" }, step.splitDescription)) : null),
                React.createElement("div", { className: "flex shrink-0 flex-col items-start gap-1 sm:items-end" },
                    !isSplit ? (React.createElement("p", { className: cn('text-sm font-bold tabular-nums', amountClass(step.direction)) }, step.amount)) : null,
                    isSplit && step.balancedLabel ? (React.createElement("span", { className: cn('inline-flex rounded-full px-2.5 py-1 text-xs font-bold', statusClass((_c = step.status) !== null && _c !== void 0 ? _c : 'success')) }, step.balancedLabel)) : null,
                    !isSplit && step.status ? (React.createElement("span", { className: cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold', statusClass(step.status)) }, formatStepStatus(String(step.status)))) : null)),
            !isSplit && ((_d = step.metaKvs) === null || _d === void 0 ? void 0 : _d.length) ? (React.createElement("div", { className: "mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" }, step.metaKvs.map((kv) => (React.createElement(DetailBox, { key: `${kv.label}-${kv.value}`, label: kv.label }, kv.value))))) : null,
            !isSplit ? (React.createElement(StepDetails, { direction: step.direction, actionLabel: step.actionLabel, balance: step.balance, status: step.hideStatusInDetails ? undefined : step.status, timestamp: step.timestamp, stepStatusLabel: stepStatusLabel, formatStepStatus: formatStepStatus })) : null,
            isSplit ? (React.createElement("div", { className: "mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" }, step.branches.map((branch, index) => {
                var _a, _b;
                return (React.createElement(BranchCard, { key: (_a = branch.id) !== null && _a !== void 0 ? _a : `${(_b = step.id) !== null && _b !== void 0 ? _b : 'split'}-${index}`, branch: branch, formatStepStatus: formatStepStatus }));
            }))) : null)));
}
function TimelineConnector({ label }) {
    return (React.createElement("div", { className: "mb-2.5 ml-14 flex items-center gap-2 text-xs font-bold text-gray-500", role: "presentation" },
        React.createElement("span", { className: "flex h-[22px] w-[22px] items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-orange-600" },
            React.createElement(ArrowDown, { className: "h-3.5 w-3.5", "aria-hidden": true })),
        React.createElement("span", null, label)));
}
const LEGEND_ITEMS = ['out', 'in', 'fee', 'system'];
export const TransactionCashFlowTimelineTree = ({ steps, title = 'Timeline tree', showLegend = true, className, stepStatusLabel = 'Status', formatStepStatus = formatStatusLabel, }) => {
    if (!steps.length) {
        return (React.createElement("section", { className: cn('rounded-lg border border-gray-200 bg-white p-4', className), "aria-label": title },
            React.createElement("h2", { className: "text-base font-semibold text-gray-900" }, title),
            React.createElement("p", { className: "mt-3 text-sm text-gray-500" }, "No cash flow steps to display.")));
    }
    return (React.createElement("section", { className: cn('rounded-lg border border-gray-200 bg-white p-4', className), "aria-label": title },
        React.createElement("div", { className: "mb-3.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" },
            React.createElement("h2", { className: "text-base font-semibold text-gray-900" }, title),
            showLegend ? (React.createElement("div", { className: "flex flex-wrap justify-start gap-1.5 sm:justify-end", "aria-label": "Direction legend" }, LEGEND_ITEMS.map((tone) => (React.createElement(DirectionPill, { key: tone, tone: tone }))))) : null),
        React.createElement("div", { className: "relative space-y-0" }, steps.map((step, index) => {
            var _a, _b;
            const stepNumber = index + 1;
            const isLast = index === steps.length - 1;
            const showLine = !isLast;
            return (React.createElement("div", { key: (_a = step.id) !== null && _a !== void 0 ? _a : `step-${index}`, className: "relative" },
                showLine ? (React.createElement("div", { className: "pointer-events-none absolute left-[21px] top-11 bottom-0 w-0.5 bg-gray-200", "aria-hidden": true })) : null,
                React.createElement(TimelineNodeCard, { step: step, stepNumber: stepNumber, stepStatusLabel: stepStatusLabel, formatStepStatus: formatStepStatus }),
                !isLast && ((_b = step.connectorAfter) === null || _b === void 0 ? void 0 : _b.label) ? (React.createElement(TimelineConnector, { label: step.connectorAfter.label })) : null));
        }))));
};
export default TransactionCashFlowTimelineTree;
